import { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { User, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import BookingForm from "@/components/BookingForm";
import PackageCard from "@/components/PackageCard";
import { PACKAGES } from "@/types/booking";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import heroImage from "@/assets/hero-balloon.jpg";
import heroVideo from "@/assets/hero-video.mp4";
import logo from "@/assets/logo.png";
import gallery1 from "@/assets/gallery/gallery-1.jpg";
import gallery2 from "@/assets/gallery/gallery-2.jpg";
import gallery3 from "@/assets/gallery/gallery-3.jpg";
import gallery4 from "@/assets/gallery/gallery-4.jpg";
import gallery5 from "@/assets/gallery/gallery-5.jpg";
import gallery6 from "@/assets/gallery/gallery-6.jpg";
import gallery7 from "@/assets/gallery/gallery-7.jpg";
import gallery8 from "@/assets/gallery/gallery-8.jpg";
import gallery9 from "@/assets/gallery/gallery-9.jpg";
import gallery10 from "@/assets/gallery/gallery-10.jpg";
import paymentMethods from "@/assets/payment-methods.png";
import aboutImage from "@/assets/about-image.jpg";
import { toast } from "sonner";
const Index = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const [selectedPackageId, setSelectedPackageId] = useState<string>("");
  const bookingFormRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    // Check auth state
    const checkAuth = async () => {
      const {
        data: {
          session
        }
      } = await supabase.auth.getSession();
      setIsAuthenticated(!!session);
      setUserEmail(session?.user?.email || "");
    };
    checkAuth();

    // Listen for auth changes
    const {
      data: {
        subscription
      }
    } = supabase.auth.onAuthStateChange((event, session) => {
      setIsAuthenticated(!!session);
      setUserEmail(session?.user?.email || "");
    });
    return () => subscription.unsubscribe();
  }, []);
  const handleLogout = async () => {
    await supabase.auth.signOut();
    toast.success("Logged out successfully");
  };
  const handlePackageSelect = (packageId: string) => {
    setSelectedPackageId(packageId);
    bookingFormRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "center"
    });
  };
  return <div className="min-h-screen">
      {/* Header */}
      <header className="py-3 px-4 w-full bg-transparent absolute top-0 left-0 right-0 z-10">
        <div className="container mx-auto flex items-center justify-center bg-transparent">
          <img alt="Hot Air Balloon Rides in Dubai" className="h-10 md:h-12 lg:h-14 object-contain max-w-full" src="/lovable-uploads/63168f7f-6a03-4d9b-a252-442f3477b0b3.png" />
        </div>
      </header>

      {/* Hero Section with Booking Form */}
      <section className="relative min-h-screen flex items-start">
        <div className="absolute inset-0 bg-cover bg-center" style={{
        backgroundImage: `url(${heroImage})`
      }}>
          <div className="absolute inset-0 bg-black/30" />
        </div>

        <div className="relative z-10 container mx-auto px-4 py-16 pt-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div className="max-w-md" ref={bookingFormRef}>
              <BookingForm selectedPackageId={selectedPackageId} />
            </div>
            <div className="max-w-2xl">
              <video className="w-full rounded-lg shadow-2xl" controls autoPlay muted loop playsInline>
                <source src={heroVideo} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>
          </div>
        </div>
      </section>

      {/* Packages Section */}
      <section className="py-16 px-4 bg-background">
        <div className="container mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold mb-12">Packages</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-6xl">
            {PACKAGES.map(pkg => <PackageCard key={pkg.id} package={pkg} onBookNow={handlePackageSelect} />)}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="relative py-20 px-4">
        <div className="absolute inset-0 bg-cover bg-center" style={{
        backgroundImage: `url(${heroImage})`
      }}>
          <div className="absolute inset-0 bg-black/50" />
        </div>
        <div className="relative z-10 container mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 text-center text-white">
            <div>
              <div className="text-6xl font-bold mb-2">500+</div>
              <div className="text-lg">Successful Flights</div>
            </div>
            <div>
              <div className="text-6xl font-bold mb-2">2000+</div>
              <div className="text-lg">Happy Guests</div>
            </div>
            <div>
              <div className="text-6xl font-bold mb-2">4.5</div>
              <div className="text-lg">Star Reviews</div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-16 px-4 bg-secondary/10">
        <div className="container mx-auto">
          <h3 className="text-sm font-medium text-muted-foreground mb-4">
            Why thousands of travelers choose us to book Hot Air Balloon Ride
          </h3>
          <div className="grid md:grid-cols-2 gap-12 items-center max-w-6xl">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
                Your Gateway
                <br />
                to the Sky
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-6">
                We are Dubai’s premier booking platform dedicated exclusively to Hot Air Balloon experiences. We don't
                just book flights; we curate memories. By partnering with the region's most strictly regulated and
                top-rated operators, we ensure that your journey is not only breathtaking but also adheres to the
                highest safety standards.
              </p>
              <p className="text-muted-foreground font-medium mb-3">We take pride in:</p>
              <ul className="space-y-3 text-muted-foreground">
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-1">•</span>
                  <span>Vetted Operators: We only work with GCAA-licensed pilots.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-1">•</span>
                  <span>Instant Confirmation: No waiting time, secure your spot immediately.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-1">•</span>
                  <span>Flexible Cancellation: Plans change? Enjoy hassle-free cancellation policies.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-1">•</span>
                  <span>No Hidden Fees: The price you see is the price you pay.</span>
                </li>
              </ul>
              <p className="text-muted-foreground leading-relaxed mt-6">
                When you fly with us, you don't just take a ride – you live a once-in-a-lifetime adventure.
              </p>
            </div>
            <div>
              <img src={aboutImage} alt="Woman enjoying hot air balloon ride over Dubai desert at sunrise" className="rounded-lg shadow-xl w-full h-auto" />
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="py-16 px-4 bg-cyan-50">
        <div className="container mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold mb-12">Gallery</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-6xl">
            <img src={gallery1} alt="Hot air balloon passengers enjoying desert views" className="rounded-lg w-full h-64 object-cover" />
            <img src={gallery2} alt="Green and white hot air balloon aerial view" className="rounded-lg w-full h-64 object-cover" />
            <img src={gallery3} alt="Green striped hot air balloon in flight" className="rounded-lg w-full h-64 object-cover" />
            <img src={gallery4} alt="Group photos in hot air balloon baskets" className="rounded-lg w-full h-64 object-cover" />
            <img src={gallery5} alt="Aerial view of hot air balloon basket over desert" className="rounded-lg w-full h-64 object-cover" />
            <img src={gallery6} alt="Families enjoying hot air balloon rides" className="rounded-lg w-full h-64 object-cover" />
            <img src={gallery7} alt="Couple celebrating hot air balloon adventure" className="rounded-lg w-full h-64 object-cover" />
            <img src={gallery8} alt="Hot air balloon ride over desert landscape" className="rounded-lg w-full h-64 object-cover" />
            <img src={gallery9} alt="Multiple hot air balloons in Dubai desert" className="rounded-lg w-full h-64 object-cover" />
            <img src={gallery10} alt="Colorful hot air balloon flight experience" className="rounded-lg w-full h-64 object-cover" />
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 px-4 bg-background">
        <div className="container mx-auto max-w-4xl">
          <h2 className="text-4xl md:text-5xl font-bold mb-12">FAQs – Hot Air Balloon Adventures</h2>

          <Accordion type="single" collapsible className="space-y-4">
            <AccordionItem value="item-1" className="border rounded-lg px-6 bg-card">
              <AccordionTrigger className="text-left font-semibold hover:no-underline">
                1. Is hot air ballooning in Dubai safe?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground">
                Absolutely. We only list operators who are licensed by the UAE General Civil Aviation Authority (GCAA).
                All pilots are internationally certified, and equipment undergoes rigorous daily checks.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-2" className="border rounded-lg px-6 bg-card">
              <AccordionTrigger className="text-left font-semibold hover:no-underline">
                2. What is the best time to book?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground">
                Balloon rides are early morning activities to catch the sunrise. We recommend booking at least 3-4 days
                in advance during the peak season (October to March) as spots fill up quickly.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-3" className="border rounded-lg px-6 bg-card">
              <AccordionTrigger className="text-left font-semibold hover:no-underline">
                3. Do you offer hotel pick-up?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground">
                Yes! All our packages, from Standard to Overnight, include pick-up and drop-off from hotels in Dubai
                city.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-4" className="border rounded-lg px-6 bg-card">
              <AccordionTrigger className="text-left font-semibold hover:no-underline">
                4. Can children fly?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground">
                Yes, children over the age of 5 are welcome to join. It is a safe and magical experience for families.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-5" className="border rounded-lg px-6 bg-card">
              <AccordionTrigger className="text-left font-semibold hover:no-underline">
                5. What happens if the weather is bad?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground">
                Your safety is the priority. If a flight is canceled due to wind or fog, you will be offered a full
                refund or the option to reschedule for the next available date.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-6" className="border rounded-lg px-6 bg-card">
              <AccordionTrigger className="text-left font-semibold hover:no-underline">
                6. What should I wear?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground">
                Dress comfortably and warmly, especially for early mornings. Sporty or casual clothes and closed shoes
                are ideal.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-7" className="border rounded-lg px-6 bg-card">
              <AccordionTrigger className="text-left font-semibold hover:no-underline">
                7. Can I bring my phone or camera?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground">
                Absolutely! Capture every moment — just make sure your device is securely strapped during the flight.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-8" className="border rounded-lg px-6 bg-card">
              <AccordionTrigger className="text-left font-semibold hover:no-underline">
                8. How can I contact hotairballoonrideindubai.com?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground">
                For inquiries, contact us at:
                <br />
                <strong>Email:</strong>{" "}
                <a href="mailto:info@hotairballoonrideindubai.com" className="text-primary hover:underline">
                  info@hotairballoonrideindubai.com
                </a>
                <br />
                <strong>Phone:</strong>{" "}
                <a href="tel:+971504209377" className="text-primary hover:underline">
                  +971 50 420 9377
                </a>
                {" | "}
                <strong>WhatsApp:</strong>{" "}
                <a href="https://wa.me/971504209377?text=Hello" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                  +971 50 420 9377
                </a>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-9" className="border rounded-lg px-6 bg-card">
              <AccordionTrigger className="text-left font-semibold hover:no-underline">
                9. What is your cancellation and refund policy?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground">
                Cancellations must be made via the same mode used for booking. You'll receive a 100% refund if cancelled 72 hours before, 50% refund if cancelled 48 hours before, and no refund for cancellations within 24 hours. For cancellations, contact{" "}
                <a href="mailto:info@hotairballoonrideindubai.com" className="text-primary hover:underline">
                  info@hotairballoonrideindubai.com
                </a>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* Payment Methods Section */}
      <section className="py-8 px-4 bg-secondary/10">
        <div className="container mx-auto text-center">
          <img src={paymentMethods} alt="Accepted payment methods: Visa, Mastercard, Tabby, Google Pay, Apple Pay" className="mx-auto max-w-2xl w-full h-auto" />
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-foreground text-background py-8 px-4">
        <div className="container mx-auto text-center">
          <p className="text-sm opacity-80">©2025 hotairballoonrideindubai.com. All Rights Reserved.</p>
        </div>
      </footer>
    </div>;
};
export default Index;